import React, { useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle, LucideImport, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Patient } from "@/lib/patient/patientListviewData";
import {
  setConsultationLoadError,
  setShowPermissionModal,
  setShowPermissionMessage,
} from "@/dispatchers/appDispatchers";
import { checkUserPermissions } from "@/utils/checkUserPermissions";
import { accessTitanverseConsultationUpsert } from "@/data/constantStrings";

interface PatientsListRowProps {
  patient: Patient;
  isDarkMode: boolean;
  currentPharmacyId: string;
  onPatientClick: (patient: Patient) => void;
  onConsultationClick?: (patient: Patient, consultation: any) => void;
  isSelected: boolean;
  onSelectPatient: (patientId: string, checked: boolean) => void;
  handleImportClick: (patient: Patient) => void;
  loadingRowId: string;
}

const isMinimumDob = (dob?: string): boolean => {
  const normalizedDob = dob?.trim();

  if (!normalizedDob) return false;

  const slashDateMatch = normalizedDob.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  const isoDateMatch = normalizedDob.match(/^(\d{4})-\d{1,2}-\d{1,2}/);
  const year = slashDateMatch?.[3] ?? isoDateMatch?.[1];

  return year ? Number(year) < 1900 : false;
};

const PatientsListRow = React.memo(function PatientsListRow({
  patient,
  isDarkMode,
  currentPharmacyId,
  onPatientClick,
  onConsultationClick,
  isSelected,
  onSelectPatient,
  handleImportClick,
  loadingRowId,
}: PatientsListRowProps) {
  const hasConsultationPermission = checkUserPermissions(
    accessTitanverseConsultationUpsert,
  );
  const handleSelect = useCallback(
    (checked: boolean) => onSelectPatient(patient.id, checked),
    [patient.id, onSelectPatient],
  );
  const showDob =
    patient.dob && patient.dob.trim() !== "" && !isMinimumDob(patient.dob);

  return (
    <tr
      className={`border-t transition-colors duration-300 hover:cursor-pointer ${
        isDarkMode
          ? "border-gray-700 hover:bg-gray-700/50"
          : "border-gray-200 hover:bg-gray-50"
      }`}
    >
      <td
        className="py-4 pl-3 pr-1 whitespace-nowrap"
        onClick={(e) => e.stopPropagation()}
      >
        <Checkbox
          checked={isSelected}
          onCheckedChange={(checked) => handleSelect(!!checked)}
          aria-label={`Select patient ${patient.name}`}
        />
      </td>
      <td className="py-4 pl-1 pr-6 whitespace-nowrap">
        <div
          className={`flex gap-2 items-center text-sm font-medium transition-colors duration-300 ${
            patient.imported === false
              ? "cursor-not-allowed text-gray-500"
              : "cursor-pointer hover:text-blue-600"
          } ${isDarkMode ? "text-white hover:text-blue-400" : "text-gray-900"}`}
          onClick={() => patient.imported !== false && onPatientClick(patient)}
        >
          {patient.name}
          {currentPharmacyId !== patient.pharmacyId &&
            patient.pharmacyName !== "" && (
              <Badge
                variant="outline"
                className={`text-[10px] px-1.5 py-0.5 font-normal ${
                  isDarkMode
                    ? "bg-gray-700 text-gray-300 border-gray-600"
                    : "bg-gray-50 text-gray-500 border-gray-300"
                }`}
              >
                {patient.pharmacyName}
              </Badge>
            )}

          {!patient.imported && (
            <Badge
              variant="secondary"
              className={`text-xs font-semibold px-2 py-0.5 ${
                isDarkMode
                  ? "bg-purple-900 text-purple-200 border-purple-700"
                  : "bg-purple-100 text-purple-800 border-purple-300"
              }`}
            >
              TITAN
            </Badge>
          )}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div
          className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
        >
          {showDob ? `(${patient.dob})` : ""} {patient.age}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div
          className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
        >
          {patient.address === "" || patient.address === "NA"
            ? ""
            : patient.address}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex flex-wrap gap-1">
          {patient?.tags && patient.tags.length > 0 ? (
            patient.tags.map((tag, tagIndex) => (
              <div
                key={tagIndex}
                style={{
                  backgroundColor: tag?.colour || "#9ca3af",
                  color: tag?.colour ? "#fff" : "#000",
                }}
                className="py-0.5 px-2 text-xs rounded-full transition-colors duration-300"
              >
                {tag?.tagName || ""}
              </div>
            ))
          ) : (
            <div></div>
          )}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div
          className={`text-sm flex items-center gap-2 ${
            isDarkMode ? "text-white" : "text-gray-900"
          }`}
        >
          <span
            className={
              patient.isConsultationActive && onConsultationClick
                ? "cursor-pointer hover:text-blue-600 hover:underline transition-colors"
                : ""
            }
            onClick={(e) => {
              if (!hasConsultationPermission) {
                e.preventDefault();
                setShowPermissionMessage(
                  "You don't have permission to start consultation.",
                );
                setShowPermissionModal(true);
                return;
              }

              if (patient.consultationId) {
                onConsultationClick(
                  patient,
                  patient?.originalPatient?.consultation.consultationId,
                );
              }
            }}
          >
            {(() => {
              if (!patient?.lastConsultation) return "";

              const date = new Date(patient.lastConsultation);

              if (isNaN(date.getTime())) return "";

              const day = date.getDate().toString().padStart(2, "0");
              const month = (date.getMonth() + 1).toString().padStart(2, "0");
              const hours = date.getHours().toString().padStart(2, "0");
              const minutes = date.getMinutes().toString().padStart(2, "0");

              return `${day}/${month} ${hours}:${minutes}`;
            })()}
          </span>

          {patient.isConsultationActive && (
            <Badge
              variant="secondary"
              className="text-green-700 transition-colors bg-green-100 cursor-pointer hover:bg-green-200 dark:bg-green-900 dark:text-green-300 dark:hover:bg-green-800"
              onClick={(e) => {
                if (onConsultationClick) {
                  e.stopPropagation();
                  onConsultationClick(
                    patient,
                    patient?.originalPatient?.consultation.consultationId,
                  );
                }
              }}
            >
              {patient.isConsultationActive}
            </Badge>
          )}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div
          className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
        >
          {patient?.practitioner === ""
            ? ""
            : patient?.practitioner?.trim()
              ? patient.practitioner
              : ""}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-2">
          <Badge
            variant={
              patient.originalPatient.isPDSEntry ? "default" : "secondary"
            }
            className={`text-xs font-medium ${
              patient.originalPatient.isPDSEntry
                ? isDarkMode
                  ? "bg-green-900 text-green-300 border-green-700 hover:bg-green-900"
                  : "bg-green-100 text-green-800 border-green-300 hover:bg-green-100"
                : isDarkMode
                  ? "bg-yellow-900 text-yellow-300 border-yellow-700 hover:bg-yellow-900"
                  : "bg-yellow-100 text-yellow-800 border-yellow-300 hover:bg-yellow-100"
            }`}
          >
            <span className="flex items-center gap-1">
              PDS
              {patient.originalPatient.isPDSEntry ? (
                <CheckCircle className="w-3 h-3" />
              ) : (
                <XCircle className="w-3 h-3" />
              )}
            </span>
          </Badge>
        </div>
      </td>
    </tr>
  );
});

export default PatientsListRow;
